<template>
  <section class="d-grid gap-3" data-test="reporte-utilidad">
    <header class="d-flex justify-content-between align-items-start flex-wrap gap-3">
      <div>
        <h2 class="h5 mb-1">Utilidad</h2>
        <p class="text-muted small mb-0">Ingresos menos egresos del período.</p>
      </div>
      <button class="btn btn-outline-dark btn-sm" data-test="btn-csv"
              :disabled="loading || !filas.length" @click="exportar">
        <i class="bi bi-download me-1"></i>Exportar CSV
      </button>
    </header>

    <div class="card shadow-sm">
      <div class="card-body">
        <div class="row g-2 align-items-end">
          <div class="col-auto">
            <label class="form-label small mb-1" for="u-desde">Desde</label>
            <input id="u-desde" v-model="filtros.fecha_desde" type="date"
                   class="form-control form-control-sm" data-test="input-desde">
          </div>
          <div class="col-auto">
            <label class="form-label small mb-1" for="u-hasta">Hasta</label>
            <input id="u-hasta" v-model="filtros.fecha_hasta" type="date"
                   class="form-control form-control-sm" data-test="input-hasta">
          </div>
          <div class="col-auto">
            <button class="btn btn-dark btn-sm" data-test="btn-buscar"
                    :disabled="loading" @click="cargar">
              {{ loading ? 'Buscando...' : 'Buscar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger mb-0" data-test="error">{{ error }}</div>

    <div v-if="totales" class="row g-3">
      <div class="col-md-4">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small">Ingresos</div>
          <div class="h5 mb-0 text-success" data-test="total-ingresos">
            {{ formatoMonto(totales.ingresos) }}
          </div>
        </div></div>
      </div>
      <div class="col-md-4">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small">Egresos</div>
          <div class="h5 mb-0 text-danger" data-test="total-egresos">
            {{ formatoMonto(totales.egresos) }}
          </div>
        </div></div>
      </div>
      <div class="col-md-4">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small">Utilidad</div>
          <div class="h5 mb-0" :class="Number(totales.utilidad) < 0 ? 'text-danger' : ''"
               data-test="total-utilidad">
            {{ formatoMonto(totales.utilidad) }}
          </div>
        </div></div>
      </div>
    </div>

    <!-- La cascada NO es un adorno: D-R3 decidió que el reporte no lleve
         leyenda de alcance en pantalla, así que el gráfico es lo único que
         muestra de qué se compone el número (RN-88). -->
    <div v-if="filas.length" class="card shadow-sm">
      <div class="card-body">
        <h3 class="h6 mb-2">De dónde sale el número</h3>
        <GraficoReporte tipo="cascada" :series="serieCascada" :alto="320" />
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="table-responsive">
        <table class="table table-sm mb-0" data-test="tabla">
          <thead>
            <tr><th>Concepto</th><th>Tipo</th><th class="text-end">Monto</th><th class="text-end">Acumulado</th></tr>
          </thead>
          <tbody>
            <tr v-for="(f, i) in filas" :key="i" :class="f.tipo === 'resultado' ? 'fw-bold' : ''">
              <td>{{ f.concepto }}</td>
              <td class="small text-muted">{{ f.tipo }}</td>
              <td class="text-end" :class="Number(f.monto) < 0 ? 'text-danger' : ''">
                {{ formatoMonto(f.monto) }}
              </td>
              <td class="text-end">{{ formatoMonto(f.hasta) }}</td>
            </tr>
            <tr v-if="!filas.length && !loading">
              <td colspan="4" class="text-center text-muted py-4" data-test="vacio">
                No hay movimientos en ese período.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script setup>
// N4 — Utilidad del período, con su cascada. RG-F7.
// SPEC RN-87, RN-88 · D-R3.
//
// ALCANCE: son los egresos que el sistema YA REGISTRA — compras, pagos a
// proveedores, egresos varios y anulaciones. **No incluye sueldos, alquiler ni
// servicios**, que en una institución son la mayor parte. El límite se comunica
// a nivel operativo, no con una leyenda acá (D-R3), y por eso la cascada es
// parte del reporte: es lo único que deja ver qué se sumó y qué se restó.
//
// Las ANULACIONES bajan en su propio escalón (iniciativa V) y esta pantalla no
// hizo falta tocarla para eso: dibuja `filas` tal como vienen. Si mañana se
// agrega otro escalón, aparece solo — el reporte decide su composición en el
// dominio, no acá.

import { ref, computed, onMounted } from 'vue'
import reportsService from '@/services/reportsService'
import GraficoReporte from '@/components/GraficoReporte.vue'
import { fechaIsoLocal } from '@/utils/fechaLocal.mjs'

const filas = ref([])
const totales = ref(null)
const loading = ref(false)
const error = ref('')

const hoy = new Date()
const primeroDelMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1)
const iso = (d) => fechaIsoLocal(d)
const filtros = ref({ fecha_desde: iso(primeroDelMes), fecha_hasta: iso(hoy) })

// `rangeBar`: cada escalón es el rango [desde, hasta] que el acumulado recorre.
// Sale directo, sin el truco de barras flotantes transparentes.
const serieCascada = computed(() => [{
  name: 'Utilidad',
  data: filas.value.map((f) => ({ x: f.concepto, y: [Number(f.desde), Number(f.hasta)] }))
}])

const formatoMonto = (v) =>
  new Intl.NumberFormat('es-PY', { maximumFractionDigits: 0 }).format(Number(v) || 0)

async function cargar () {
  loading.value = true
  error.value = ''
  try {
    const data = await reportsService.getUtilidad(filtros.value)
    filas.value = data.filas
    totales.value = data.totales
  } catch (e) {
    error.value = e?.response?.data?.message || 'No se pudo cargar el reporte.'
  } finally {
    loading.value = false
  }
}

async function exportar () {
  try {
    await reportsService.descargarCsv('utilidad', filtros.value, 'utilidad')
  } catch (e) {
    error.value = 'No se pudo exportar el CSV.'
  }
}

onMounted(cargar)
</script>
